import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronUp, PencilLine, Download, Loader2, LogOut, User, Mail, ShieldCheck, Calendar, FileText, Compass, KeyRound, UserX, UserCheck, Trash2, CalendarRange, AlertTriangle } from "lucide-react";
import { toast, Toaster } from "sonner";
import BrandHeader from "../components/BrandHeader";
import CoachLogoutButton from "../components/CoachLogoutButton";
import CoachCheckinHistory from "../components/CoachCheckinHistory";
import CoachNotes from "../components/CoachNotes";
import { MindsetBlock } from "./Results";
import CurrentRoadmapEditor, { CoachEditedBadge } from "../components/CurrentRoadmapEditor";
import { getCoachClient, getCoachCurrentRoadmap, pdfDownloadUrl, setCoachClientStatus, sendCoachClientPasswordReset, deleteCoachClient } from "../lib/api";
import { sydDate, sydDateTime, sydTime } from "../lib/time";

const SLEEP_LABELS = { "<5": "Less than 5", "5-7": "5 to 7 hours", "7-9": "7 to 9 hours", "9+": "9 or more" };
const BROKEN_LABELS = { every_night: "Every night", every_other_night: "Every other night", few_times_week: "A few times a week", few_times_month: "A few times a month", never: "Never" };
const ENERGY = { "1-3": "1-3 I feel like a zombie", "3-5": "3-5 I'm the queen of surviving", "5-7": "5-7 Average energy", "7-9": "7-9 Above average energy", "10": "10 Energiser bunny" };
const STRESS = { "1-3": "1-3 All Panic, No Disco", "3-5": "3-5 Survival Mode", "5-7": "5-7 In and out of control", "7-9": "7-9 Feeling Zen", "10": "10 Peak Peace" };
const EMO = { daily: "Almost daily", few_times_week: "A few times a week", few_times_month: "A few times a month", never: "Never" };
const PACE = { steady: "Steady and enjoyable", balance: "Happy balance", fastest: "Fastest fat loss" };

function row(label, value) {
  return { label, value: value === 0 ? "0" : (value || "None") };
}
function buildRows(a) {
  if (!a) return [];
  return [
    row("Age / Height / Weight", `${a.age} yrs · ${a.height_cm} cm · ${a.weight_kg} kg`),
    row("Average steps per day", `${(a.avg_steps || 0).toLocaleString()}${a.steps_note ? `, ${a.steps_note}` : ""}`),
    row("Workouts outside walking", `${a.workouts} (${a.workouts_per_week}/week)`),
    row("Typical bed / wake time", `${a.bed_time} → ${a.wake_time}`),
    row("Sleep routine notes", a.sleep_note),
    row("Last 2 hours before bed", a.last_two_hours),
    row("Hours of sleep most nights", SLEEP_LABELS[a.sleep_hours] || a.sleep_hours),
    row("Broken sleep frequency", BROKEN_LABELS[a.broken_sleep] || a.broken_sleep),
    row("Daily energy out of 10", ENERGY[a.energy_level] || a.energy_level),
    row("Daily stress out of 10", STRESS[a.stress_level] || a.stress_level),
    row("Eating habits & cravings", a.eating_habits),
    row("Emotional eating frequency", EMO[a.emotional_eating] || a.emotional_eating),
    row("Current daily me-time", a.me_time_current),
    row("Realistic daily commitment", a.me_time_realistic),
    row("Main goal", a.main_goal),
    row("Pace chosen", PACE[a.pace] || a.pace),
  ];
}
const fmtDate = (iso) => sydDateTime(iso, "None");

export default function CoachClient() {
  const { client_id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [current, setCurrent] = useState(null); // effective roadmap (original + coach overrides)
  const [editing, setEditing] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [params] = useSearchParams();
  const section = params.get("section");

  useEffect(() => {
    if (state.loading || !section) return;
    const id = { roadmap: "client-roadmap-section", notes: "coach-notes", history: "coach-checkin-history" }[section];
    const t = setTimeout(() => document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    if (section === "roadmap") setDetailsOpen(true);
    return () => clearTimeout(t);
  }, [state.loading, section]);

  useEffect(() => {
    getCoachCurrentRoadmap(client_id).then(setCurrent).catch(() => setCurrent(null));
  }, [client_id]);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    getCoachClient(client_id)
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) setState({ loading: false, data: null, error: { kind: "auth" } });
        else if (status === 404) setState({ loading: false, data: null, error: { kind: "notfound" } });
        else setState({ loading: false, data: null, error: { kind: "server", message: err?.message || "Couldn't load this client." } });
      });
    return () => { cancelled = true; };
  }, [client_id]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-brand-base flex items-center justify-center">
        <Loader2 className="text-brand-primary animate-spin" size={32} strokeWidth={1.5} />
      </div>
    );
  }

  if (state.error?.kind === "auth") {
    return (
      <div className="min-h-screen bg-brand-base">
        <BrandHeader small right={<CoachLogoutButton />} />
        <main className="max-w-3xl mx-auto px-6 py-16 text-center">
          <LogOut className="mx-auto text-brand-primary" strokeWidth={1.4} size={32} />
          <p className="mt-4 font-serif text-2xl text-brand-ink">Your coach session has expired.</p>
          <button
            type="button"
            onClick={() => navigate("/coach-login", { replace: true })}
            className="btn-primary inline-flex items-center gap-2 mt-6"
            data-testid="client-profile-signin-btn"
          >
            <LogOut size={16} strokeWidth={1.8} /> Sign in again
          </button>
        </main>
      </div>
    );
  }

  if (state.error?.kind === "notfound" || !state.data) {
    return (
      <div className="min-h-screen bg-brand-base">
        <BrandHeader small right={<CoachLogoutButton />} />
        <main className="max-w-3xl mx-auto px-6 py-16 text-center">
          <p className="font-serif text-2xl text-brand-ink">Client not found.</p>
          <Link to="/coach" className="btn-ghost inline-flex items-center gap-2 mt-6" data-testid="client-profile-back-btn">
            <ArrowLeft size={16} /> Back to Client Accounts
          </Link>
        </main>
      </div>
    );
  }

  const c = state.data;
  const roadmap = (c.roadmaps && c.roadmaps[0]) || null;
  const rows = roadmap ? buildRows(roadmap.answers) : [];
  const eff = current?.effective || {};
  const ovr = new Set(current?.overridden_fields || []);
  const val = (k) => (ovr.has(k) ? eff[k] : roadmap?.[k]);
  const goalNow = ovr.has("current_goal") ? eff.current_goal : roadmap?.main_goal;
  const pdfUrl = roadmap ? pdfDownloadUrl(roadmap.id) : "";
  const filename = `her-aligned-roadmap-${(c.preferred_name || c.full_name || "client").toLowerCase().replace(/\s+/g, "-")}.pdf`;
  const isInactive = c.status === "inactive";
  const backTo = isInactive ? "/coach?view=inactive" : "/coach";

  const handleDownloadPdf = () => {
    if (!roadmap?.has_pdf) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 1500);
  };

  const applyPatch = (patch) => setState((s) => ({ ...s, data: { ...s.data, ...patch } }));

  return (
    <div className="min-h-screen bg-brand-base" data-testid="client-profile-page">
      <Toaster richColors position="top-center" />
      <BrandHeader small right={<CoachLogoutButton />} />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <Link to={backTo} className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="client-profile-back-link">
          <ArrowLeft size={16} strokeWidth={1.8} /> Back to {isInactive ? "Inactive Clients" : "Client Accounts"}
        </Link>

        {/* Identity block — SOURCE OF TRUTH: clients collection */}
        <section className="surface-card mt-6 p-8" data-testid="client-identity-block">
          <div className="label-eyebrow flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-brand-primary" />
            HER Aligned Client Profile
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-brand-ink mt-4" data-testid="client-full-name">
            {c.full_name || "Unnamed Client"}
          </h1>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-base">
            <IdentityRow icon={<User size={16} strokeWidth={1.8} />} label="Prefers to be called" value={c.preferred_name || "None"} testid="client-preferred-name" />
            <IdentityRow icon={<Mail size={16} strokeWidth={1.8} />} label="Email" value={c.email} testid="client-email" />
            <IdentityRow icon={<ShieldCheck size={16} strokeWidth={1.8} />} label="Status" value={<StatusPill status={c.status} />} testid="client-status" />
            <IdentityRow icon={<Calendar size={16} strokeWidth={1.8} />} label="Account created" value={fmtDate(c.created_at)} testid="client-created-at" />
            <IdentityRow
              icon={<CalendarRange size={16} strokeWidth={1.8} />}
              label="Active client tenure"
              value={
                <span>
                  <span className="font-serif text-2xl text-brand-ink">Week {Math.max(1, c.tenure_week || 1)}</span>
                  {isInactive && <span className="ml-2 text-xs text-brand-ink-muted" data-testid="client-tenure-frozen-note">frozen while Inactive</span>}
                </span>
              }
              testid="client-tenure-week"
            />
          </div>
        </section>

        <CoachActions client={c} onPatch={applyPatch} navigate={navigate} />

        {/* Personalised Roadmap section */}
        <section className="mt-8" data-testid="client-roadmap-section">
          <div className="label-eyebrow flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-brand-primary" />
            Personalised Roadmap
          </div>
          <h2 className="font-serif text-3xl text-brand-ink mt-3">Roadmap</h2>

          {!roadmap ? (
            <div className="surface-card p-8 mt-6" data-testid="client-roadmap-incomplete">
              <div className="flex items-start gap-4">
                <Compass className="text-brand-primary shrink-0 mt-1" size={22} strokeWidth={1.5} />
                <div>
                  <p className="font-serif text-xl text-brand-ink">Roadmap not yet completed.</p>
                  <p className="mt-2 text-brand-ink-muted text-base">
                    {c.preferred_name || c.full_name || "This client"} has an authenticated HER Aligned account but hasn&apos;t submitted her Personalised Roadmap yet. She&apos;ll be able to progress to Weekly Check-In setup once the Roadmap is complete.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6 space-y-6" data-testid="client-roadmap-complete">
              {/* Summary + actions (always visible) */}
              <div className="surface-card p-8" data-testid="client-roadmap-summary">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <p className="label-eyebrow">Submitted</p>
                    <p className="font-serif text-2xl text-brand-ink mt-1">{fmtDate(roadmap.created_at)}</p>
                    {goalNow ? (
                      <p className="mt-3 text-brand-ink-muted italic max-w-2xl" data-testid="client-roadmap-goal">&ldquo;{goalNow}&rdquo;{ovr.has("current_goal") && <CoachEditedBadge />}</p>
                    ) : null}
                    {ovr.has("current_goal") && roadmap.main_goal ? (
                      <p className="mt-1 text-xs text-brand-ink-muted" data-testid="client-roadmap-original-goal">Original stated goal: &ldquo;{roadmap.main_goal}&rdquo;</p>
                    ) : null}
                    {ovr.size > 0 && <p className="mt-2 text-xs text-[#8a6234]" data-testid="client-roadmap-override-count">{ovr.size} coach-edited field{ovr.size > 1 ? "s" : ""} in the current Roadmap{current?.updated_at ? ` · last edited ${sydDate(current.updated_at)}` : ""}</p>}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button type="button" onClick={() => setEditing((v) => !v)} className="btn-ghost inline-flex items-center gap-2" data-testid="client-edit-roadmap-btn">
                      <PencilLine size={18} strokeWidth={1.8} /> {editing ? "Close editor" : "Edit Current Roadmap"}
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadPdf}
                      disabled={!roadmap.has_pdf}
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="client-download-pdf-btn"
                      title={roadmap.has_pdf ? "" : "The PDF hasn't been generated yet."}
                    >
                      <Download size={18} strokeWidth={1.8} />
                      {roadmap.has_pdf ? "Download PDF" : "PDF unavailable"}
                    </button>
                  </div>
                </div>
                <button type="button" onClick={() => setDetailsOpen((v) => !v)} aria-expanded={detailsOpen}
                        className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-brand-ink hover:text-[#C8965A]"
                        data-testid="client-roadmap-details-toggle">
                  {detailsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />} {detailsOpen ? "Hide Roadmap Details" : "View Roadmap Details"}
                </button>
              </div>

              {editing && <CurrentRoadmapEditor clientId={c.id} onSaved={(d) => { setCurrent(d); }} onClose={() => setEditing(false)} />}

              {detailsOpen && (
              <div className="space-y-6" data-testid="client-roadmap-details">
              {/* Personalised targets (effective) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SummaryCard title="Calorie range"      value={val("calorie_range") || "None"} edited={ovr.has("calorie_range")} original={roadmap.calorie_range} />
                <SummaryCard title="Protein range"      value={val("protein_range") || "None"} edited={ovr.has("protein_range")} original={roadmap.protein_range} />
                <SummaryCard title="Daily step range"   value={val("step_range") || "None"} edited={ovr.has("step_range")} original={roadmap.step_range} />
                <SummaryCard title="Training frequency" value={val("training_frequency") || "None"} edited={ovr.has("training_frequency")} original={roadmap.training_frequency} />
              </div>

              {/* Recommendations (effective) */}
              <RoadmapProse title="Nutrition guidance" body={val("nutrition_advice")} testid="client-nutrition" edited={ovr.has("nutrition_advice")} original={roadmap.nutrition_advice} />
              <RoadmapProse title="Fitness guidance" body={val("fitness_advice")} testid="client-fitness" edited={ovr.has("fitness_advice")} original={roadmap.fitness_advice} />

              <HabitList title="Sleep habits" items={val("sleep_habits")} testid="client-sleep-habits" edited={ovr.has("sleep_habits")} />
              <HabitList title="Stress regulation habits" items={val("stress_habits")} testid="client-stress-habits" edited={ovr.has("stress_habits")} />
              {/* Mindset: same deterministic mapping the client sees on Results/PDF (saved selections, no AI) */}
              <div data-testid="client-mindset-section">
                <MindsetBlock patterns={roadmap.answers?.mindset_patterns} root={roadmap.answers?.mindset_root} guidance={ovr.has("mindset_guidance") ? eff.mindset_guidance : null} animate={false} />
              </div>

              {/* Answers (original, never editable) */}
              <section className="surface-card p-8" data-testid="client-answers">
                <div className="label-eyebrow flex items-center gap-3">
                  <FileText size={14} strokeWidth={1.8} />
                  Roadmap questionnaire answers (original)
                </div>
                <div className="mt-4 divide-y divide-brand-ink/10">
                  {rows.map((r) => (
                    <div key={r.label} className="grid grid-cols-1 md:grid-cols-[minmax(0,220px)_1fr] gap-2 py-3">
                      <div className="text-sm text-brand-ink-muted">{r.label}</div>
                      <div className="text-base text-brand-ink whitespace-pre-wrap">{r.value}</div>
                    </div>
                  ))}
                </div>
              </section>
              </div>
              )}
            </div>
          )}
        </section>

        <CoachCheckinHistory clientId={c.id} />

        {/* Placeholder for future stages — deliberately empty per Stage 2A scope */}
      </main>
    </div>
  );
}

function IdentityRow({ icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-brand-primary mt-1">{icon}</span>
      <div>
        <p className="text-xs uppercase tracking-widest text-brand-ink-muted">{label}</p>
        <p className="text-base text-brand-ink mt-1" data-testid={testid}>{value}</p>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || "active").toLowerCase();
  const tone =
    s === "active" ? "bg-emerald-50 text-emerald-800 ring-emerald-200" :
    s === "inactive" ? "bg-brand-ink/5 text-brand-ink ring-brand-ink/20" :
    "bg-red-50 text-red-800 ring-red-200";
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ring-1 ${tone}`} data-testid="client-status-pill">{s}</span>;
}

function CoachActions({ client: c, onPatch, navigate }) {
  const isInactive = c.status === "inactive";
  const [busy, setBusy] = useState(null); // 'status' | 'reset' | 'delete'
  const [confirmStatus, setConfirmStatus] = useState(false);
  const [resetSentAt, setResetSentAt] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteWord, setDeleteWord] = useState("");
  const name = c.preferred_name || c.full_name || "this client";

  const changeStatus = async (next) => {
    setBusy("status");
    try {
      const r = await setCoachClientStatus(c.id, next);
      onPatch({ status: r.status, status_changed_at: r.status_changed_at, tenure_week: r.tenure_week });
      setConfirmStatus(false);
      toast.success(next === "inactive" ? `${name} is now Inactive. Her sessions have been signed out.` : `${name} is Active again. She can log in with her existing credentials.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update status.");
    } finally {
      setBusy(null);
    }
  };

  const sendReset = async () => {
    setBusy("reset");
    try {
      const r = await sendCoachClientPasswordReset(c.id);
      setResetSentAt(new Date());
      toast.success(r.message || `Password reset email sent to ${c.email}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the password reset email.");
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    if (deleteWord !== "DELETE") return;
    setBusy("delete");
    try {
      await deleteCoachClient(c.id);
      toast.success(`${c.full_name || c.email} was permanently deleted.`);
      navigate("/coach", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete this client.");
      setBusy(null);
    }
  };

  return (
    <section className="mt-8" data-testid="coach-actions-section">
      <div className="label-eyebrow flex items-center gap-3">
        <span className="inline-block h-px w-10 bg-brand-primary" />
        Coach actions
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status */}
        <div className="surface-card p-6" data-testid="coach-status-card">
          <h3 className="font-serif text-2xl text-brand-ink">Client status</h3>
          <p className="mt-2 text-sm text-brand-ink-muted leading-relaxed">
            {isInactive
              ? "Inactive: her record is kept for your records but she cannot log in or use any HER Aligned client functionality. Reactivating restores her existing account instantly."
              : "Active: she can log in, access her Personalised Roadmap and Weekly Check-Ins. Making her Inactive signs her out everywhere immediately and blocks access until you reactivate her."}
          </p>
          {isInactive ? (
            <button
              type="button"
              onClick={() => changeStatus("active")}
              disabled={busy === "status"}
              className="btn-primary inline-flex items-center gap-2 mt-5 disabled:opacity-60"
              data-testid="client-reactivate-btn"
            >
              {busy === "status" ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} strokeWidth={1.8} />} Reactivate client
            </button>
          ) : confirmStatus ? (
            <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="client-deactivate-confirm">
              <p className="text-sm text-amber-900">Make <strong>{name}</strong> Inactive? She will be signed out now and won&apos;t be able to log in until you reactivate her. Nothing is deleted.</p>
              <div className="mt-3 flex gap-3">
                <button type="button" onClick={() => setConfirmStatus(false)} disabled={busy === "status"} className="btn-ghost border border-brand-border rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] font-bold" data-testid="client-deactivate-cancel-btn">Cancel</button>
                <button type="button" onClick={() => changeStatus("inactive")} disabled={busy === "status"} className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] font-bold bg-brand-ink text-white disabled:opacity-60" data-testid="client-deactivate-confirm-btn">
                  {busy === "status" ? <Loader2 size={14} className="animate-spin" /> : <UserX size={14} strokeWidth={1.8} />} Yes, make Inactive
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmStatus(true)}
              className="btn-ghost border border-brand-border rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-bold inline-flex items-center gap-2 mt-5"
              data-testid="client-deactivate-btn"
            >
              <UserX size={14} strokeWidth={1.8} /> Make Inactive
            </button>
          )}
        </div>

        {/* Password reset */}
        <div className="surface-card p-6" data-testid="coach-reset-card">
          <h3 className="font-serif text-2xl text-brand-ink">Password reset</h3>
          <p className="mt-2 text-sm text-brand-ink-muted leading-relaxed">
            Emails a secure, single-use reset link to <span className="text-brand-ink">{c.email}</span>. Her current password is never shown and is not changed until she sets a new one.
          </p>
          <button
            type="button"
            onClick={sendReset}
            disabled={busy === "reset" || isInactive}
            title={isInactive ? "Reactivate this client before sending a password reset." : ""}
            className="btn-primary inline-flex items-center gap-2 mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="client-send-reset-btn"
          >
            {busy === "reset" ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} strokeWidth={1.8} />} Send Password Reset
          </button>
          {isInactive && (
            <p className="mt-3 text-xs text-brand-ink-muted" data-testid="client-reset-inactive-note">Unavailable while Inactive. Reactivate her first.</p>
          )}
          {resetSentAt && (
            <p className="mt-3 text-sm text-emerald-800" data-testid="client-reset-sent-confirmation">
              Password reset email sent to {c.email} at {sydTime(resetSentAt)}.
            </p>
          )}
        </div>
      </div>

      <CoachNotes clientId={c.id} />

      {/* Danger zone */}
      <div className="mt-6 rounded-3xl border border-red-200 bg-red-50/40 p-6" data-testid="coach-danger-zone">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-red-700 shrink-0 mt-0.5" size={20} strokeWidth={1.8} />
          <div className="flex-1">
            <h3 className="font-serif text-2xl text-brand-ink">Delete Client Permanently</h3>
            <p className="mt-2 text-sm text-brand-ink-muted leading-relaxed">
              Only for test, duplicate or mistaken accounts. A paying client who leaves HER Aligned should be marked <strong>Inactive</strong> instead so her history is retained. Permanent deletion removes her account, Roadmap, PDF and Weekly Check-In setup and <strong>cannot be undone</strong>.
            </p>
            <button
              type="button"
              onClick={() => { setDeleteWord(""); setDeleteOpen(true); }}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-red-300 px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-bold text-red-800 hover:bg-red-100 transition-colors"
              data-testid="client-delete-btn"
            >
              <Trash2 size={14} strokeWidth={1.8} /> Delete Client Permanently
            </button>
          </div>
        </div>
      </div>

      {deleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" data-testid="client-delete-modal">
          <div className="surface-card max-w-md w-full p-6 md:p-8">
            <h3 className="font-serif text-2xl text-brand-ink">Permanently delete {c.full_name || c.email}?</h3>
            <p className="mt-3 text-sm text-brand-ink leading-relaxed">
              This removes her Client Account, Personalised Roadmap, archived PDF, Weekly Check-In setup, sessions and reset links. <strong>This cannot be undone.</strong>
            </p>
            <label className="label-eyebrow block mt-5 mb-2" htmlFor="delete-word">Type DELETE to confirm</label>
            <input
              id="delete-word"
              type="text"
              className="input-warm"
              value={deleteWord}
              onChange={(e) => setDeleteWord(e.target.value)}
              autoComplete="off"
              data-testid="client-delete-confirm-input"
            />
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <button type="button" onClick={() => setDeleteOpen(false)} disabled={busy === "delete"} className="btn-ghost border border-brand-border rounded-full px-6 py-2 text-sm uppercase tracking-[0.2em] font-bold" data-testid="client-delete-cancel-btn">Cancel</button>
              <button
                type="button"
                onClick={doDelete}
                disabled={busy === "delete" || deleteWord !== "DELETE"}
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-2 text-sm uppercase tracking-[0.2em] font-bold bg-red-700 text-white hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="client-delete-confirm-btn"
              >
                {busy === "delete" ? <Loader2 size={14} strokeWidth={2} className="animate-spin" /> : <Trash2 size={14} strokeWidth={1.8} />}
                {busy === "delete" ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function SummaryCard({ title, value, edited, original }) {
  return (
    <div className="surface-card p-6">
      <p className="label-eyebrow">{title}{edited && <CoachEditedBadge />}</p>
      <p className="mt-2 font-serif text-2xl text-brand-ink">{value}</p>
      {edited && original ? <p className="mt-1 text-xs text-brand-ink-muted">Original: {original}</p> : null}
    </div>
  );
}

function RoadmapProse({ title, body, testid, edited, original }) {
  return (
    <section className="surface-card p-8" data-testid={testid}>
      <h3 className="font-serif text-2xl text-brand-ink">{title}{edited && <CoachEditedBadge />}</h3>
      <p className="mt-3 text-base leading-relaxed text-brand-ink whitespace-pre-wrap">{body || "None"}</p>
      {edited && original ? <details className="mt-3 text-xs text-brand-ink-muted"><summary className="cursor-pointer">Original generated guidance</summary><p className="mt-2 whitespace-pre-wrap">{original}</p></details> : null}
    </section>
  );
}

function HabitList({ title, items, testid, edited }) {
  return (
    <section className="surface-card p-8" data-testid={testid}>
      <h3 className="font-serif text-2xl text-brand-ink">{title}{edited && <CoachEditedBadge />}</h3>
      <ol className="mt-4 space-y-4">
        {(items || []).map((h, i) => (
          <li key={i}>
            <p className="font-medium text-brand-ink">{h.title}</p>
            <p className="text-base text-brand-ink-muted leading-relaxed mt-1">{h.explanation}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}
